import React, { useEffect, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { SectorBasedGHGInventory } from './SectorBasedGHGInventory';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { fetchGHGInventoryData, GHGInventoryResult } from './GHGInventorySupabase';

type ChartType = 'stacked-area' | 'line' | 'pie';

interface SectorBasedGHGInventoryWithMetricsProps {
  location: string;
}

export const SectorBasedGHGInventoryWithMetrics: React.FC<SectorBasedGHGInventoryWithMetricsProps> = ({ location }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('stacked-area');
  const [activeView, setActiveView] = useState<string>('overview');
  const [ghgData, setGhgData] = useState<GHGInventoryResult | null>(null);

  // Load GHG inventory immediately. VMT is preloaded in background by LocationContext when location is set;
  // fetchGHGInventoryData uses cached VMT if ready, otherwise fetches VMT directly (no blocking).
  useEffect(() => {
    if (!location) return;

    const loadGHGData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchGHGInventoryData(location);
        setGhgData(result);
      } catch (err) {
        console.error('Error loading GHG inventory data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load GHG inventory data');
      } finally {
        setLoading(false);
      }
    };

    loadGHGData();
  }, [location]);

  if (loading) {
    return (
      <ChartLayout
        title="Sector-Based GHG Inventory"
        chartContent={
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#6b7280' }}>
              Loading GHG inventory data...
            </div>
          </div>
        }
        description="Greenhouse gas emissions inventory organized by economic sectors and activities."
        chartType="line"
        keyMetrics={[]}
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sector-based inventories help identify which economic sectors contribute most to emissions and where interventions can be most effective."
          >
            <div className="methodology-details-simple">
              <h5>Economic Impact</h5>
              <p>Understanding sector contributions helps prioritize climate action in areas with highest emissions impact.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Targeting</h5>
              <p>Sector-based data enables targeted policies for specific industries and activities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Sector-Based GHG Inventory Methodology"
            description="This inventory follows standard protocols for tracking emissions by economic sector and activity type."
            dataSources={[
              'California Air Resources Board (CARB)',
              'Local utility data',
              'Transportation surveys',
              'Building energy data'
            ]}
            methodology={[
              '<strong>Data Collection:</strong> Gather emissions data from major sectors including transportation, energy, buildings, and waste',
              '<strong>Activity Data:</strong> Collect activity data such as vehicle miles traveled, energy consumption, and waste generation',
              '<strong>Emission Factors:</strong> Apply appropriate emission factors to convert activity data to CO₂ equivalent emissions',
              '<strong>Aggregation:</strong> Sum emissions by sector and year to create comprehensive inventory'
            ]}
          />
        }
        overviewContent={
          <OverviewContent
            title="Sector-Based GHG Inventory Overview"
            description="This inventory tracks greenhouse gas emissions organized by economic sectors and activities."
          >
            <p>The sector-based approach categorizes emissions by economic activity, making it easier to identify the largest sources and develop targeted reduction strategies.</p>
            <p>Key sectors typically include transportation, energy, buildings, waste, and other activities specific to the community.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Sector-based inventories provide the foundation for developing comprehensive climate action plans."
          >
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Sector-based data helps policymakers prioritize actions and allocate resources effectively.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Progress Tracking</h5>
              <p>Regular inventories enable communities to track progress toward emission reduction goals.</p>
            </div>
          </RelevanceContent>
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        overviewSectionId="ghg_inventory"
        overviewChartId="sector_based_ghg"
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
        title="Sector-Based GHG Inventory"
        description="Greenhouse gas emissions inventory organized by economic sectors and activities."
        chartType="line"
        keyMetrics={[]}
        chartContent={<div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>Error loading sector-based GHG data: {error}</div>}
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sector-based inventories help identify which economic sectors contribute most to emissions and where interventions can be most effective."
          >
            <div className="methodology-details-simple">
              <h5>Economic Impact</h5>
              <p>Understanding sector contributions helps prioritize climate action in areas with highest emissions impact.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Targeting</h5>
              <p>Sector-based data enables targeted policies for specific industries and activities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Sector-Based GHG Inventory Methodology"
            description="This inventory follows standard protocols for tracking emissions by economic sector and activity type."
            dataSources={[
              'California Air Resources Board (CARB)',
              'Local utility data',
              'Transportation surveys',
              'Building energy data'
            ]}
            methodology={[
              '<strong>Data Collection:</strong> Gather emissions data from major sectors including transportation, energy, buildings, and waste',
              '<strong>Activity Data:</strong> Collect activity data such as vehicle miles traveled, energy consumption, and waste generation',
              '<strong>Emission Factors:</strong> Apply appropriate emission factors to convert activity data to CO₂ equivalent emissions',
              '<strong>Aggregation:</strong> Sum emissions by sector and year to create comprehensive inventory'
            ]}
          />
        }
        overviewContent={
          <OverviewContent
            title="Sector-Based GHG Inventory Overview"
            description="This inventory tracks greenhouse gas emissions organized by economic sectors and activities."
          >
            <p>The sector-based approach categorizes emissions by economic activity, making it easier to identify the largest sources and develop targeted reduction strategies.</p>
            <p>Key sectors typically include transportation, energy, buildings, waste, and other activities specific to the community.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Sector-based inventories provide the foundation for developing comprehensive climate action plans."
          >
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Sector-based data helps policymakers prioritize actions and allocate resources effectively.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Progress Tracking</h5>
              <p>Regular inventories enable communities to track progress toward emission reduction goals.</p>
            </div>
          </RelevanceContent>
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        overviewSectionId="ghg_inventory"
        overviewChartId="sector_based_ghg"
      />
    );
  }

  const keyMetrics = (
    <KeyMetrics>
      <KeyMetric 
        value={ghgData?.totalEmissions ? `${ghgData.totalEmissions.toLocaleString()} MT CO₂e` : '—'}
        label={`Total Emissions (${ghgData?.latestYear || '2023'})`}
        color="#1e3a8a"
      />
      <KeyMetric 
        value={ghgData?.lightDutyEmissions ? `${ghgData.lightDutyEmissions.toLocaleString()} MT CO₂e` : '—'}
        label="Light Duty Vehicles"
        color="#10b981"
      />
      <KeyMetric 
        value={ghgData?.heavyDutyEmissions ? `${ghgData.heavyDutyEmissions.toLocaleString()} MT CO₂e` : '—'}
        label="Heavy Duty Vehicles"
        color="#f59e0b"
      />
    </KeyMetrics>
  );

  return (
    <ChartLayout
      title="Sector-Based GHG Inventory"
      description="Greenhouse gas emissions inventory organized by economic sectors and activities."
      chartType="line"
      keyMetrics={keyMetrics}
      chartContent={<SectorBasedGHGInventory location={location} chartType={chartType} setChartType={setChartType} ghgData={ghgData} />}
      chartSelector={{
        options: [
          { value: 'stacked-area', label: 'Stacked Area' },
          { value: 'line', label: 'Line Chart' },
          { value: 'pie', label: 'Pie Chart (2023)' }
        ],
        value: chartType,
        onChange: (value) => setChartType(value as ChartType)
      }}
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="GHG inventories must consider equity implications across different communities and demographic groups."
        >
          <div className="space-y-3">
            <p>
              <strong>Disproportionate Impacts:</strong> Some communities may face higher exposure to emissions sources and greater vulnerability to climate impacts.
            </p>
            <p>
              <strong>Access to Solutions:</strong> Climate solutions must be accessible to all income levels to ensure equitable participation in emissions reductions.
            </p>
            <p>
              <strong>Community Benefits:</strong> Climate investments should prioritize communities that have historically borne disproportionate environmental burdens.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Comprehensive GHG Inventory Methodology"
          description="Complete sector-based greenhouse gas emissions inventory using activity data, emission factors, and standardized calculation methods for all major emission sources."
          dataSources={[
            'VMT data from replica_vmt_by_mode table',
            'EMFAC county data for ghg_per_mile factors',
            'Heavy duty vehicle data from fleet_ghg table',
            'EMFAC final estimation data for scaling factors',
            'Natural gas consumption from ng_res_format and ng_com_format',
            'Electricity consumption from elec_res_format and elec_com_format',
            'Dynamic carbon intensity data from eGRID utilities database and Power Content Labels (CCA)',
            'Waste emissions: Calculated from total_waste tonnage × county-level emission factors',
            'Offroad emissions: County-level data from offroad_emissions table',
            'Estimated other emission sources (industrial, agriculture, etc.)'
          ]}
          methodology={[
            'Light duty vehicles: VMT × consistent ghg_per_mile factor from EMFAC county data',
            'Heavy duty vehicles: Direct fleet_ghg data when available, EMFAC scaling for historical years',
            'Natural gas residential/commercial: total_therms × 0.005291 (5291 grams/therm ÷ 1,000,000)',
            'Electricity commercial/residential: total_kwh × dynamic_carbon_intensity (year-specific g CO₂/kWh ÷ 1,000,000)',
            'Carbon intensity: Location-matched utility data from eGRID and CCA Power Content Labels',
            'Carbon intensity precedence: CCA Power Content Label data takes priority over eGRID data for same year',
            'Carbon intensity fallback: 200 g CO₂/kWh used when dynamic data unavailable',
            'Waste: waste_tons × emission_factor (county-level SUM(ghg_emissions)/SUM(waste_tons) from landfill_emissions_full, or California statewide average)',
            'Offroad: Direct emissions data from offroad_emissions table (county-level, unique per geofips/year)',
            'All estimates use 2% annual reduction trend to reflect efficiency improvements',
            'All emissions reported in metric tons CO₂ equivalent (MT CO₂e)',
            'Total inventory includes all major emission sources for comprehensive coverage'
          ]}
        />
      }
      overviewContent={
        <OverviewContent
          title="GHG Inventory Overview"
          description="Comprehensive greenhouse gas emissions tracking by economic sector."
        >
          <p>
            {location} has total greenhouse gas emissions of {ghgData?.totalEmissions?.toLocaleString()} metric tons CO₂ equivalent in {ghgData?.latestYear || '2023'}. 
            Light duty vehicles account for {ghgData?.lightDutyEmissions?.toLocaleString()} MT CO₂e ({ghgData?.lightDutyEmissions && ghgData?.totalEmissions ? Math.round((ghgData.lightDutyEmissions / ghgData.totalEmissions) * 100) : 0}%), 
            while heavy duty vehicles contribute {ghgData?.heavyDutyEmissions?.toLocaleString()} MT CO₂e ({ghgData?.heavyDutyEmissions && ghgData?.totalEmissions ? Math.round((ghgData.heavyDutyEmissions / ghgData.totalEmissions) * 100) : 0}%). 
            The inventory includes all major emission sources with dynamic carbon intensity calculations for electricity emissions.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action"
          description="GHG inventory data provides the foundation for climate action planning and emissions reduction strategies."
        >
          <div className="space-y-3">
            <p>
              <strong>Baseline Establishment:</strong> GHG inventories establish baseline emissions levels needed for setting reduction targets and tracking progress.
            </p>
            <p>
              <strong>Sector Prioritization:</strong> Understanding which sectors contribute most to emissions helps prioritize climate investments and policy interventions.
            </p>
            <p>
              <strong>Progress Tracking:</strong> Regular inventory updates enable monitoring of emission trends and evaluation of climate action effectiveness.
            </p>
          </div>
        </RelevanceContent>
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      location={location}
      overviewSectionId="ghg_inventory"
      overviewChartId="sector_based_ghg"
    />
  );
};